import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Divider,
  Avatar,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';

const CandidateQueue = ({ setLoading, setError, setSuccess }) => {
  const [nominations, setNominations] = useState([]);
  const [elections, setElections] = useState([]);
  const [openElections, setOpenElections] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedElectionId, setSelectedElectionId] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [proposeElectionId, setProposeElectionId] = useState('');
  const [proposeCandidateId, setProposeCandidateId] = useState('');
  const [proposeBio, setProposeBio] = useState('');
  const [proposing, setProposing] = useState(false);
  
  // Fetch nominations and elections
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const nominationsRes = await axios.get('/api/nominations/');
        const electionsRes = await axios.get('/api/elections/');
        const usersRes = await axios.get('/api/users/');

        setNominations(nominationsRes.data);
        setElections(electionsRes.data);
        setUsers(usersRes.data || []);

        // Fetch only the current open election; ignore 404 without surfacing error
        try {
          const currentRes = await axios.get('/api/elections/current');
          const open = currentRes?.data ? [currentRes.data] : [];
          setOpenElections(open);
          if (open.length > 0) {
            setProposeElectionId(String(open[0].id));
          }
        } catch (e) {
          setOpenElections([]);
        }
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to fetch data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [setLoading, setError]);
  
  // Handle nomination approval/rejection
  const handleUpdateStatus = async (id, status) => {
    try {
      setProcessingId(id);
      await axios.put(`/api/nominations/${id}/status`, { status });
      
      // Update local state
      setNominations(nominations.map(nom => 
        nom.id === id ? { ...nom, status } : nom
      ));
      
      setSuccess(`Application ${status === 'approved' ? 'approved' : 'rejected'} successfully!`);
      setProcessingId(null);
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${status} application`);
      setProcessingId(null);
    }
  };
  
  // Filter nominations by election and status
  const filteredNominations = selectedElectionId 
    ? nominations.filter(nom => nom.election_id === parseInt(selectedElectionId))
    : nominations;
  
  const pendingNominations = filteredNominations.filter(nom => nom.status === 'pending');
  const approvedNominations = filteredNominations.filter(nom => nom.status === 'approved');
  const rejectedNominations = filteredNominations.filter(nom => nom.status === 'rejected');
  
  const handlePropose = async () => {
    try {
      if (!proposeElectionId || !proposeCandidateId || !proposeBio.trim()) {
        setError('Please select an election, a candidate, and enter a short bio.');
        return;
      }
      setProposing(true);
      await axios.post('/api/nominations/admin', {
        election_id: parseInt(proposeElectionId),
        candidate_id: parseInt(proposeCandidateId),
        bio: proposeBio.trim()
      });
      setSuccess('Candidate proposed successfully!');
      setProposing(false);
      setProposeBio('');
      // Refresh nominations list to include the newly created nomination with joined data
      const refreshed = await axios.get('/api/nominations/');
      setNominations(refreshed.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to propose candidate');
      setProposing(false);
    }
  };
  
  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
          Candidate Applications Queue
        </Typography>
        
        {/* Propose Candidate */}
        <Card 
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: '14px',
            background: 'rgba(127, 140, 156, 0.5)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Propose Candidate (Admin)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Election</InputLabel>
                <Select
                  value={proposeElectionId}
                  onChange={(e) => setProposeElectionId(e.target.value)}
                  label="Election"
                  disabled={openElections.length === 0}
                >
                  {openElections.length === 0 ? (
                    <MenuItem value="" disabled>
                      No open election
                    </MenuItem>
                  ) : (
                    openElections.map(e => (
                      <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Candidate</InputLabel>
                <Select
                  value={proposeCandidateId}
                  onChange={(e) => setProposeCandidateId(e.target.value)}
                  label="Candidate"
                  disabled={openElections.length === 0}
                >
                  {users.map(u => {
                    const displayName = u.name || u.email;
                    // Extract acronym inside parentheses from friendly BU label, e.g., "High-tech Consulting (HTC)" -> "HTC"
                    const buAcr = u.business_unit
                      ? (u.business_unit.match(/\(([^)]+)\)/)?.[1] || '')
                      : '';
                    const bu = buAcr ? ` (${buAcr})` : '';
                    const joinDate = u.joining_date ? ` - ${new Date(u.joining_date).toLocaleDateString()}` : '';
                    return (
                      <MenuItem key={u.id} value={u.id}>
                        {`${displayName}${bu}${joinDate}`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Short bio"
                value={proposeBio}
                onChange={(e) => setProposeBio(e.target.value)}
                fullWidth
                multiline
                minRows={1}
                disabled={openElections.length === 0}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handlePropose}
                  disabled={proposing || openElections.length === 0}
                  sx={{
                    backgroundColor: '#009999',
                    '&:hover': { backgroundColor: '#007f7f' },
                    borderRadius: '10px',
                    textTransform: 'none',
                    px: 2
                  }}
                >
                  {proposing ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Propose'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Election Filter */}
        <FormControl fullWidth variant="outlined" sx={{ mb: 3, maxWidth: 300 }}>
          <InputLabel>Filter by Election</InputLabel>
          <Select
            value={selectedElectionId}
            onChange={(e) => setSelectedElectionId(e.target.value)}
            label="Filter by Election"
          >
            <MenuItem value="">
              <em>All Elections</em>
            </MenuItem>
            {elections.map(election => (
              <MenuItem key={election.id} value={election.id}>
                {election.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {pendingNominations.length === 0 ? (
          <Card 
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '14px',
              background: 'rgba(112, 128, 144, 0.4)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" sx={{ py: 3 }}>
              No pending nominations to review
            </Typography>
          </Card>
        ) : (
          <AnimatePresence>
            {pendingNominations.map((nomination, index) => (
              <motion.div
                key={nomination.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Card 
                  sx={{
                    mb: 3,
                    borderRadius: '14px',
                    background: 'rgba(127, 140, 156, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                      borderColor: '#99D6D6'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar 
                            sx={{ 
                              width: 56, 
                              height: 56,
                              bgcolor: '#702963'
                            }}
                          >
                            <PersonIcon fontSize="large" />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {(() => {
                                const u = nomination.candidate;
                                if (!u) return 'Candidate';
                                const displayName = u.name || u.email || 'Candidate';
                                const buAcr = u.business_unit
                                  ? (u.business_unit.match(/\(([^)]+)\)/)?.[1] || '')
                                  : '';
                                const bu = buAcr ? ` (${buAcr})` : '';
                                const jd = u.joining_date ? ` - ${new Date(u.joining_date).toLocaleDateString()}` : '';
                                return `${displayName}${bu}${jd}`;
                              })()}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Election: {nomination.election_name || "Unknown Election"}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </Grid>
                      
                      <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Chip 
                          label="Pending Review" 
                          color="warning" 
                          variant="filled"
                          sx={{ fontWeight: 500 }}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {nomination.bio}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                          Submitted on {new Date(nomination.created_at).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                          <Tooltip title="Reject Nomination">
                            <IconButton
                              onClick={() => handleUpdateStatus(nomination.id, 'rejected')}
                              disabled={processingId === nomination.id}
                              sx={{
                                color: '#AB0E0E',
                                border: '2px solid #AB0E0E',
                                p: 1,
                                borderRadius: '12px',
                                '&:hover': {
                                  backgroundColor: 'rgba(171, 14, 14, 0.1)'
                                }
                              }}
                            >
                              {processingId === nomination.id ? (
                                <CircularProgress size={24} sx={{ color: '#AB0E0E' }} />
                              ) : (
                                <ThumbDownIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Approve Nomination">
                            <IconButton
                              onClick={() => handleUpdateStatus(nomination.id, 'approved')}
                              disabled={processingId === nomination.id}
                              sx={{
                                color: '#009999',
                                border: '2px solid #009999',
                                p: 1,
                                borderRadius: '12px',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 153, 153, 0.1)'
                                }
                              }}
                            >
                              {processingId === nomination.id ? (
                                <CircularProgress size={24} sx={{ color: '#009999' }} />
                              ) : (
                                <ThumbUpIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
        
        <Typography variant="h6" sx={{ mt: 5, mb: 3, fontWeight: 500 }}>
          Processed Nominations
        </Typography>
        
        <Grid container spacing={3}>
          {[...approvedNominations, ...rejectedNominations].map((nomination) => (
            <Grid item xs={12} md={6} key={nomination.id}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '14px',
                  background: 'rgba(127, 140, 156, 0.5)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderLeft: nomination.status === 'approved' 
                    ? '4px solid #009999' 
                    : '4px solid #AB0E0E'
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      {(() => {
                        const u = nomination.candidate;
                        if (!u) return 'Candidate';
                        const displayName = u.name || u.email || 'Candidate';
                        const buAcr = u.business_unit
                          ? (u.business_unit.match(/\(([^)]+)\)/)?.[1] || '')
                          : '';
                        const bu = buAcr ? ` (${buAcr})` : '';
                        const jd = u.joining_date ? ` - ${new Date(u.joining_date).toLocaleDateString()}` : '';
                        return `${displayName}${bu}${jd}`;
                      })()}
                    </Typography>
                    <Chip 
                      label={nomination.status === 'approved' ? 'Approved' : 'Rejected'}
                      color={nomination.status === 'approved' ? 'success' : 'error'}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {nomination.election_name || "Unknown Election"}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                    {nomination.bio.length > 150 
                      ? `${nomination.bio.substring(0, 150)}...` 
                      : nomination.bio}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          
          {approvedNominations.length === 0 && rejectedNominations.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ textAlign: 'center', opacity: 0.7, py: 3 }}>
                No processed nominations yet
              </Typography>
            </Grid>
          )}
        </Grid>
      </motion.div>
    </Box>
  );
};

export default CandidateQueue;
